import { ReactElement } from 'react';

import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import dayjs from 'dayjs';

import useUsgaCourseRefresh from '../../dataHooks/useUsgaCourseRefresh';
import { useUsgaCourseRefreshMutators } from '../../dataMutators/useUsgaCourseRefreshMutators';
import { USGA_COURSE_REFRESH_STATUSES } from '../../types/UsgaCourseRefresh';

import type { UsgaCourseRefresh } from '../../types/UsgaCourseRefresh';

const isRunning = (refresh: UsgaCourseRefresh | null) =>
  refresh?.status === USGA_COURSE_REFRESH_STATUSES.REQUESTED || refresh?.status === USGA_COURSE_REFRESH_STATUSES.RUNNING;

function describe(refresh: UsgaCourseRefresh | null): string {
  if (!refresh) return 'Never run';
  if (isRunning(refresh)) return 'Refreshing…';
  const finished = refresh.finishedAt && 'toDate' in refresh.finishedAt
    ? dayjs(refresh.finishedAt.toDate()).format('MMM D, h:mm A')
    : '';
  if (refresh.status === USGA_COURSE_REFRESH_STATUSES.ERROR) return `Failed ${finished}: ${refresh.message ?? 'unknown error'}`;
  const failed = refresh.failedCount ? `, ${refresh.failedCount} couldn't be read` : '';
  return `Last ${finished}: ${refresh.courseCount ?? 0} courses, ${refresh.changedCount ?? 0} changed${failed}`;
}

/**
 * God menu: re-reads every USGA-linked course's posting season — the same refresh that runs every
 * October 1. Stays open after a click so the status updates in place.
 */
export default function GodRefreshCoursesMenuItem(): ReactElement {
  const { refresh } = useUsgaCourseRefresh();
  const { requestUsgaCourseRefresh } = useUsgaCourseRefreshMutators();

  return (
    <MenuItem disabled={isRunning(refresh)} onClick={() => requestUsgaCourseRefresh()}>
      <Stack>
        <Typography variant="bodyMedium">Refresh courses</Typography>
        <Typography variant="bodySmall" color="text.secondary">{describe(refresh)}</Typography>
      </Stack>
    </MenuItem>
  );
}
